import { useEffect, useMemo, useRef, useState } from 'react'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faExpand, faCompress, faClockRotateLeft, faBolt, faXmark, faSun, faMoon } from '@fortawesome/free-solid-svg-icons'
import useFetch from '../../hooks/useFetch'
import useLocale from '../../hooks/useLocale'
import { urlApi } from '../../config'
import LoadingIndicator from '../../common/LoadingIndicator'
import LiveMap from './LiveMap'
import StatsBar from './StatsBar'
import TripsPanel from './TripsPanel'
import UpcomingPanel from './UpcomingPanel'
import TripDrawer from './TripDrawer'
import useNow from './useNow'
import useLobbyDetails from './useLobbyDetails'
import useTerritories from './useTerritories'
import { toTrips, isToday, isInTransit, isUpcomingToday, progress, passengerCount } from './tripUtils'
import useAppTheme from '../../hooks/useTheme'
import './live.css'

const REFRESH_MS = 60 * 1000
const pad = n => String(n).padStart(2, '0')
const toLocalInput = ms => {
  const d = new Date(ms)
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`
}

const TimeControl = ({ isLive, now, liveNow, onSet, onLive }) => {
  const [open, setOpen] = useState(false)
  return (
    <div className='relative'>
      <button
        onClick={() => setOpen(o => !o)}
        className='flex items-center gap-2 h-10 px-3 rounded-xl border transition-colors'
        style={isLive
          ? { background: 'var(--panel)', borderColor: 'var(--border)', color: 'var(--text-soft)' }
          : { background: 'var(--amber-soft)', borderColor: 'var(--amber)', color: 'var(--amber)' }}
        title='Ver un horario anterior'
      >
        <FontAwesomeIcon icon={faClockRotateLeft} className='text-sm' />
        <span className='hidden sm:inline text-xs font-semibold'>{isLive ? 'Horario' : 'Histórico'}</span>
      </button>
      {open && (
        <div className='absolute right-0 mt-2 z-30 w-64 p-3 rounded-2xl border' style={{ background: 'var(--panel)', borderColor: 'var(--border)', boxShadow: 'var(--shadow-pop)' }}>
          <div className='text-[11px] uppercase tracking-wider mb-2' style={{ color: 'var(--text-faint)' }}>Ver el mapa a un horario</div>
          <input
            type='datetime-local'
            max={toLocalInput(liveNow)}
            value={toLocalInput(now)}
            onChange={e => { const ms = new Date(e.target.value).getTime(); if (!Number.isNaN(ms)) onSet(ms) }}
            className='w-full rounded-xl px-3 py-2 text-sm outline-none border'
            style={{ background: 'var(--panel-soft)', borderColor: 'var(--border)', color: 'var(--text)' }}
          />
          <button
            onClick={() => { onLive(); setOpen(false) }}
            disabled={isLive}
            className='mt-2 w-full flex items-center justify-center gap-2 h-9 rounded-xl text-sm font-semibold text-white transition-all disabled:opacity-40'
            style={{ background: 'var(--coral)' }}
          >
            <FontAwesomeIcon icon={faBolt} className='text-xs' /> Volver a vivo
          </button>
        </div>
      )}
    </div>
  )
}

const LiveDashboard = () => {
  const locale = useLocale()
  const { loading, data, refetch } = useFetch(`${urlApi}/admin/lobbies${locale ? `?locale=${locale}` : ''}`, {}, { lazy: false })
  const liveNow = useNow(1000)
  const [viewTime, setViewTime] = useState(null)
  const isLive = viewTime == null
  const now = isLive ? liveNow : viewTime

  const { theme: appTheme } = useAppTheme()
  const [theme, setTheme] = useState(appTheme)
  useEffect(() => { setTheme(appTheme) }, [appTheme])

  const [ready, setReady] = useState(false)
  const [isFullscreen, setIsFullscreen] = useState(false)
  const [openTripId, setOpenTripId] = useState(null)
  const [upcomingOpen, setUpcomingOpen] = useState(false)
  const [showUpcomingMap, setShowUpcomingMap] = useState(false)
  const rootRef = useRef(null)

  useEffect(() => { if (!loading) setReady(true) }, [loading])
  useEffect(() => { const id = setInterval(() => refetch(), REFRESH_MS); return () => clearInterval(id) }, [refetch])
  useEffect(() => {
    const onChange = () => setIsFullscreen(Boolean(document.fullscreenElement))
    document.addEventListener('fullscreenchange', onChange)
    return () => document.removeEventListener('fullscreenchange', onChange)
  }, [])

  const toggleFullscreen = () => {
    if (document.fullscreenElement) document.exitFullscreen?.()
    else rootRef.current?.requestFullscreen?.()
  }

  const hasPax = t => passengerCount(t) > 0
  const trips = useMemo(() => toTrips(data), [data])
  const todayTrips = useMemo(() => trips.filter(t => isToday(t, now) && hasPax(t)), [trips, now])
  const liveTrips = useMemo(
    () => trips.filter(t => isInTransit(t, now) && hasPax(t)).sort((a, b) => progress(b, now) - progress(a, now)),
    [trips, now]
  )
  const upcomingTrips = useMemo(
    () => trips.filter(t => isUpcomingToday(t, now) && hasPax(t)).sort((a, b) => (a?.Journey?.departure_time > b?.Journey?.departure_time ? 1 : -1)),
    [trips, now]
  )
  const territories = useTerritories(trips, liveNow)
  const passengersToday = useMemo(() => todayTrips.reduce((acc, t) => acc + passengerCount(t), 0), [todayTrips])

  const detailsById = useLobbyDetails(liveTrips.map(t => t._id))
  const enrichedLiveTrips = useMemo(
    () => liveTrips.map(t => {
      const d = detailsById[t._id]
      if (!d) return t
      return { ...t, Owner_info: { ...t.Owner_info, ...d.Owner_info }, companions: Array.isArray(d.companions) && d.companions.length ? d.companions : t.companions }
    }),
    [liveTrips, detailsById]
  )

  const openTrip = useMemo(
    () => enrichedLiveTrips.find(t => t._id === openTripId) || upcomingTrips.find(t => t._id === openTripId),
    [enrichedLiveTrips, upcomingTrips, openTripId]
  )

  const clock = new Date(now).toLocaleTimeString('es-AR', { hour: '2-digit', minute: '2-digit', second: '2-digit' })
  const histDate = !isLive ? new Date(now).toLocaleDateString('es-AR', { day: '2-digit', month: 'short' }) : null

  if (!ready) {
    return (
      <div className={`live-root live-${theme} w-full h-full flex items-center justify-center`} style={{ background: 'var(--bg)' }}>
        <LoadingIndicator size={26} />
      </div>
    )
  }

  const iconBtn = 'w-10 h-10 flex items-center justify-center rounded-xl border transition-colors'
  const iconBtnStyle = { background: 'var(--panel)', borderColor: 'var(--border)', color: 'var(--text-soft)' }

  return (
    <div ref={rootRef} className={`live-root live-${theme} relative w-full h-full overflow-hidden`} style={{ background: 'var(--bg-grad)', color: 'var(--text)' }}>
      <div className='relative flex flex-col h-full p-4 sm:p-5 gap-4'>
        {/* Encabezado */}
        <div className='flex items-center gap-3 shrink-0'>
          <div className='flex items-center gap-2.5'>
            <img src={require('../../assets/icons/logo.png')} alt='Viatik' className='h-7 w-auto select-none' />
            <span className='flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[11px] font-bold' style={{ background: 'var(--coral-soft)', color: 'var(--coral)' }}>
              {isLive ? (
                <span className='relative flex h-2 w-2'>
                  <span className='live-ping absolute inline-flex h-full w-full rounded-full' style={{ background: 'var(--coral)' }} />
                  <span className='relative inline-flex rounded-full h-2 w-2' style={{ background: 'var(--coral)' }} />
                </span>
              ) : (
                <span className='w-2 h-2 rounded-full' style={{ background: 'var(--amber)' }} />
              )}
              {isLive ? 'EN VIVO' : `HISTÓRICO · ${histDate}`}
            </span>
          </div>
          <div className='ml-auto flex items-center gap-2.5'>
            <span className='tabular-nums text-lg sm:text-xl font-bold' style={{ color: 'var(--text)' }}>{clock}</span>
            <TimeControl isLive={isLive} now={now} liveNow={liveNow} onSet={setViewTime} onLive={() => setViewTime(null)} />
            <button onClick={() => setTheme(t => (t === 'light' ? 'dark' : 'light'))} className={iconBtn} style={iconBtnStyle} title='Cambiar tema'>
              <FontAwesomeIcon icon={theme === 'light' ? faMoon : faSun} />
            </button>
            <button onClick={toggleFullscreen} className={iconBtn} style={iconBtnStyle} title='Pantalla completa'>
              <FontAwesomeIcon icon={isFullscreen ? faCompress : faExpand} />
            </button>
          </div>
        </div>

        {!isLive && (
          <div className='shrink-0 -mt-1 flex items-center gap-2 text-xs rounded-xl px-3 py-2' style={{ color: 'var(--amber)', background: 'var(--amber-soft)', border: '1px solid var(--amber)' }}>
            <FontAwesomeIcon icon={faClockRotateLeft} />
            <span>Estás viendo un momento pasado. Los autos muestran dónde estaban a esa hora.</span>
            <button onClick={() => setViewTime(null)} className='ml-auto flex items-center gap-1.5 font-semibold hover:underline'>
              <FontAwesomeIcon icon={faXmark} className='text-[10px]' /> Volver a vivo
            </button>
          </div>
        )}

        <div className='shrink-0'>
          <StatsBar todayTrips={todayTrips} liveCount={liveTrips.length} passengersToday={passengersToday} />
        </div>

        <div className='flex-1 min-h-0 flex flex-col lg:flex-row gap-4'>
          <div className='relative flex-1 min-h-0 rounded-3xl border overflow-hidden' style={{ borderColor: 'var(--border)', background: 'var(--map-water)', boxShadow: 'var(--shadow-card)' }}>
            <LiveMap
              liveTrips={enrichedLiveTrips}
              upcomingTrips={upcomingTrips}
              showUpcoming={showUpcomingMap}
              now={now}
              isLive={isLive}
              selectedId={openTripId}
              onOpenTrip={setOpenTripId}
              territories={territories}
            />
          </div>
          <div className='w-full lg:w-[392px] shrink-0 min-h-0 flex flex-col gap-3'>
            <div className='flex-1 min-h-0 rounded-3xl border p-3.5' style={{ borderColor: 'var(--border)', background: 'var(--panel-soft)', boxShadow: 'var(--shadow-card)' }}>
              <TripsPanel liveTrips={enrichedLiveTrips} now={now} onOpenTrip={setOpenTripId} />
            </div>
            <UpcomingPanel
              trips={upcomingTrips}
              open={upcomingOpen}
              setOpen={setUpcomingOpen}
              showOnMap={showUpcomingMap}
              setShowOnMap={setShowUpcomingMap}
              onOpenTrip={setOpenTripId}
            />
          </div>
        </div>
      </div>

      {openTripId && <TripDrawer tripId={openTripId} fallback={openTrip} now={now} onClose={() => setOpenTripId(null)} />}
    </div>
  )
}

export default LiveDashboard
